'use client';

import * as React from 'react';
import { Button } from '../button';
import { Input } from '../input';
import { Label } from '../label';
import { SimpleDialog } from '../simple-dialog';
import { Switch } from '../switch';

type SaveViewDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (name: string, isShared: boolean) => void;
  defaultName?: string;
  defaultShared?: boolean;
};

// gloss
const SaveViewDialog = ({
  open,
  onOpenChange,
  onSave,
  defaultName = '',
  defaultShared = true,
}: SaveViewDialogProps) => {
  const [name, setName] = React.useState(defaultName);
  const [isShared, setIsShared] = React.useState(defaultShared);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setName(defaultName);
      setIsShared(defaultShared);
      setIsSubmitting(false);
    }
  }, [open, defaultName, defaultShared]);

  const trimmedName = name.trim();

  // why: must guard against re-entry on save — a double-click/double-Enter before the async
  // why: dialog close creates duplicate views
  const handleSave = () => {
    if (!trimmedName || isSubmitting) return;
    setIsSubmitting(true);
    onSave(trimmedName, isShared);
  };

  return (
    <SimpleDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Save View"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!trimmedName || isSubmitting}>
            Save View
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="save-view-name">View Name</Label>
          <Input
            id="save-view-name"
            autoFocus
            placeholder="e.g. High Score Champions"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleSave();
            }}
            maxLength={100}
          />
        </div>
        <div className="flex items-center gap-3">
          <Switch id="save-view-shared" checked={isShared} onCheckedChange={setIsShared} />
          <Label htmlFor="save-view-shared" className="font-normal text-muted-foreground">
            {isShared ? 'Shared with team' : 'Private to me'}
          </Label>
        </div>
      </div>
    </SimpleDialog>
  );
};

export type { SaveViewDialogProps };
export { SaveViewDialog };
